/**
 * ProfileUpdateForm Component
 *
 * Phase 2 form for professional information in the provider registration workflow.
 * Includes fields for provider number, practice name, and phone with country selection.
 * Features country-specific validation and phone number formatting.
 *
 * @module components/provider-registration/ProfileUpdateForm
 */

import React from 'react';
import { cn } from '@/utils/cn';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { FormField } from '@/components/ui/FormField';
import { TextArea } from '@/components/ui/TextArea';
import { ProfilePhotoUpload } from '@/components/ui/ProfilePhotoUpload';
import { useProviderProfileUpdateFormState } from '@/hooks/useProviderProfileUpdateFormState';
import { useTheme } from '@/hooks/useTheme';
import { PROVIDER_ROLE_OPTIONS } from '@/constants';
import type { ProfileFormState } from '@/types/registration';


/**
 * ProfileUpdateForm component props interface
 */
export interface ProfileUpdateFormProps {
  /** Provider ID from registration */
  providerId: string;
  /** Form submission handler */
  onSubmit: (data: ProfileFormState['values']) => Promise<void>;
  /** Loading state */
  isLoading?: boolean;
  /** Initial form data */
  initialData?: Partial<ProfileFormState['values']>;
  /** Form submission error */
  submitError?: string;
  /** Custom class name */
  className?: string;
  /** Disable form */
  disabled?: boolean;
  /** Form mode - determines button text and behavior */
  mode?: 'registration' | 'edit';
}

/**
 * ProfileUpdateForm component for Phase 2 of provider registration
 *
 * @example
 * ```tsx
 * <ProfileUpdateForm
 *   providerId="provider-123"
 *   onSubmit={handleProfileSubmit}
 *   isLoading={isSubmitting}
 *   submitError={submitError}
 * />
 * ```
 */
export const ProfileUpdateForm: React.FC<ProfileUpdateFormProps> = ({
  providerId,
  onSubmit,
  isLoading = false,
  initialData = {},
  submitError,
  className,
  disabled = false,
  mode = 'registration'
}) => {
  const { isDark } = useTheme();

  // Use the custom hook for form state management
  const {
    formData,
    fieldRefs,
    getFieldValidation,
    getProviderNumberHelperText,
    handleFieldChange,
    handlePhotoChange,
    handleFieldBlur,
    handleSubmit
  } = useProviderProfileUpdateFormState({
    providerId,
    initialData,
    onSubmit,
    disabled,
    isLoading
  });

  return (
    <div className={cn('w-full', className)}>
      {/* Form Header */}
      <div className="text-left mb-8">
        <h1 className={cn(
          "text-2xl font-bold mb-2",
          isDark ? "text-secondary-100" : "text-secondary-900"
        )}>
          Complete Your Profile
        </h1>
        <p className={cn(
          isDark ? "text-secondary-400" : "text-secondary-600"
        )}>
          Add your professional information to finish setting up your account
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6" noValidate>
        {/* Personal Information Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* First Name Field */}
          <FormField
            label="First Name"
            required
            {...getFieldValidation('firstName')}
          >
            <Input
              ref={(el) => { fieldRefs.current.firstName = el; }}
              type="text"
              value={formData.firstName}
              onChange={(e) => handleFieldChange('firstName', e.target.value)}
              onBlur={() => handleFieldBlur('firstName')}
              placeholder="Enter your first name"
              disabled={disabled || isLoading}
              required
              fullWidth
              autoComplete="given-name"
            />
          </FormField>

          {/* Last Name Field */}
          <FormField
            label="Last Name"
            required
            {...getFieldValidation('lastName')}
          >
            <Input
              ref={(el) => { fieldRefs.current.lastName = el; }}
              type="text"
              value={formData.lastName}
              onChange={(e) => handleFieldChange('lastName', e.target.value)}
              onBlur={() => handleFieldBlur('lastName')}
              placeholder="Enter your last name"
              disabled={disabled || isLoading}
              required
              fullWidth
              autoComplete="family-name"
            />
          </FormField>
        </div>

        {/* Role Field */}
        <FormField
          label="Role"
          required
          helperText="Select your medical role or specialty"
          {...getFieldValidation('role')}
        >
          <Select
            ref={(el) => { fieldRefs.current.role = el; }}
            value={formData.role}
            onValueChange={(value) => handleFieldChange('role', value)}
            onBlur={() => handleFieldBlur('role')}
            disabled={isLoading}
            state={getFieldValidation('role').error ? 'error' : 'default'}
            options={PROVIDER_ROLE_OPTIONS.map(option => ({
              value: option.value,
              label: option.label,
              disabled: option.disabled
            }))}
            required
          />
        </FormField>

        {/* Provider Number Field */}
        <FormField
          label="Provider Number"
          required
          {...getFieldValidation('providerNumber')}
          helperText={getProviderNumberHelperText()}
        >
          <Input
            ref={(el) => { fieldRefs.current.providerNumber = el; }}
            type="text"
            value={formData.providerNumber}
            onChange={(e) => handleFieldChange('providerNumber', e.target.value)}
            onBlur={() => handleFieldBlur('providerNumber')}
            placeholder="Enter your provider number"
            disabled={disabled || isLoading}
            maxLength={10}
            required
            fullWidth
          />
        </FormField>

        {/* Practice Name Field */}
        <FormField
          label="Practice Name"
          required
          {...getFieldValidation('practiceName')}
          helperText="The name of your medical practice or clinic"
        >
          <Input
            ref={(el) => { fieldRefs.current.practiceName = el; }}
            type="text"
            value={formData.practiceName}
            onChange={(e) => handleFieldChange('practiceName', e.target.value)}
            onBlur={() => handleFieldBlur('practiceName')}
            placeholder="Enter your practice name"
            disabled={disabled || isLoading}
            required
            fullWidth
          />
        </FormField>

        {/* Practice Address Field */}
        <FormField
          label="Practice Address"
          required
          {...getFieldValidation('practiceAddress')}
          helperText="The full address of your medical practice or clinic"
        >
          <Input
            ref={(el) => { fieldRefs.current.practiceAddress = el; }}
            type="text"
            value={formData.practiceAddress}
            onChange={(e) => handleFieldChange('practiceAddress', e.target.value)}
            onBlur={() => handleFieldBlur('practiceAddress')}
            placeholder="Enter your practice address"
            disabled={disabled || isLoading}
            required
            fullWidth
          />
        </FormField>

        {/* Bio Field */}
        <FormField
          label="Bio"
          {...getFieldValidation('bio')}
          helperText="Tell patients about yourself and your practice (optional, max 500 characters)"
        >
          <div className="space-y-1">
            <TextArea
              ref={(el) => { fieldRefs.current.bio = el; }}
              value={formData.bio || ''}
              onChange={(e) => handleFieldChange('bio', e.target.value)}
              onBlur={() => handleFieldBlur('bio')}
              placeholder="Share your background, specialties, and approach to patient care..."
              disabled={disabled || isLoading}
              maxLength={500}
              rows={4}
              className={cn(
                getFieldValidation('bio').error
                  ? isDark
                    ? "border-red-500 focus:ring-red-500"
                    : "border-red-500 focus:ring-red-500"
                  : ""
              )}
            />
            <div className={cn(
              "text-xs text-right",
              isDark ? "text-secondary-400" : "text-secondary-500"
            )}>
              {(formData.bio || '').length}/500 characters
            </div>
          </div>
        </FormField>


        {/* Profile Photo Section */}
        <div className="space-y-4">
          <h3 className={cn(
            'text-lg font-semibold',
            isDark ? 'text-white' : 'text-gray-900'
          )}>
            Profile Photo
          </h3>

          <FormField
            label="Profile Photo"
            {...getFieldValidation('photo')}
            helperText="Optional: Upload a professional photo for your profile"
          >
            <ProfilePhotoUpload
              value={formData.photo ? URL.createObjectURL(formData.photo) : undefined}
              onChange={(photoData) => {
                if (photoData) {
                  // Convert base64 data URL to File object
                  fetch(photoData)
                    .then(res => res.blob())
                    .then(blob => {
                      const file = new File([blob], 'profile-photo.jpg', { type: 'image/jpeg' });
                      handlePhotoChange(file);
                    })
                    .catch(() => handlePhotoChange(undefined));
                } else {
                  handlePhotoChange(undefined);
                }
              }}
              disabled={disabled || isLoading}
              error={getFieldValidation('photo').error}
            />
          </FormField>
        </div>

        {/* Submit Error */}
        {submitError && (
          <div className={cn(
            "p-4 border rounded-lg",
            isDark ? "bg-red-900/20 border-red-800" : "bg-red-50 border-red-200"
          )}>
            <div className="flex items-start">
              <svg className={cn(
                "w-5 h-5 mt-0.5 mr-3 flex-shrink-0",
                isDark ? "text-red-400" : "text-red-600"
              )} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className={cn(
                  "text-sm font-medium",
                  isDark ? "text-red-200" : "text-red-800"
                )}>
                  Profile Update Failed
                </h3>
                <p className={cn(
                  "text-sm mt-1",
                  isDark ? "text-red-300" : "text-red-700"
                )}>
                  {submitError}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          variant="primary"
          size="md"
          loading={isLoading}
          disabled={disabled}
          className="w-full"
        >
          {isLoading
            ? 'Updating Profile...'
            : mode === 'edit'
              ? 'Save Changes'
              : 'Complete Registration'
          }
        </Button>

        {/* Information Notice */}
        <div className={cn(
          "p-4 border rounded-lg",
          isDark ? "bg-blue-900/20 border-blue-800" : "bg-blue-50 border-blue-200"
        )}>
          <div className="flex items-start">
            <svg className={cn(
              "w-5 h-5 mt-0.5 mr-3 flex-shrink-0",
              isDark ? "text-blue-400" : "text-blue-600"
            )} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className={cn(
                "text-sm font-medium",
                isDark ? "text-blue-200" : "text-blue-800"
              )}>
                What happens next?
              </h3>
              <p className={cn(
                "text-sm mt-1",
                isDark ? "text-blue-300" : "text-blue-700"
              )}>
                After completing your profile, our team will review your credentials within 1 business day.
                You'll receive an email confirmation once your account is approved.
              </p>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProfileUpdateForm;